import React, { createContext, FunctionComponent, useCallback, useContext, useMemo, useState } from 'react';

type AccordionState = Record<string, Record<string, boolean>>;

type TrainingAccordionContextValue = {
  getExpanded: (farmName: string, key: string) => boolean;
  setExpanded: (farmName: string, key: string, isExpanded: boolean) => void;
  resetAll: () => void;
};

const TrainingAccordionContext = createContext<TrainingAccordionContextValue>({
  getExpanded: () => false,
  setExpanded: () => {},
  resetAll: () => {},
});

export const TrainingAccordionProvider: FunctionComponent = ({ children }) => {
  const [state, setState] = useState<AccordionState>({});

  const getExpanded = useCallback(
    (farmName: string, key: string) => state[farmName]?.[key] ?? false,
    [state]
  );

  const setExpanded = useCallback((farmName: string, key: string, isExpanded: boolean) => {
    setState((prev) => ({
      ...prev,
      [farmName]: { ...prev[farmName], [key]: isExpanded },
    }));
  }, []);

  const resetAll = useCallback(() => setState({}), []);

  const value = useMemo(() => ({ getExpanded, setExpanded, resetAll }), [getExpanded, setExpanded, resetAll]);

  return <TrainingAccordionContext.Provider value={value}>{children}</TrainingAccordionContext.Provider>;
};

export const useTrainingAccordion = () => useContext(TrainingAccordionContext);
